import format from 'date-fns/format';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';

import {
  REPORTING_GRAIN_MAX_BUCKETS,
  ReportingGrain,
  ReportingTimezone,
} from '@eluve/api-contract';
import {
  Button,
  Calendar,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Stack,
  Text,
} from '@eluve/components';

/** "Label  Value ▾" trigger content shared by the labeled toolbar controls. */
export const ControlLabel: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <>
    <Text size="sm" color="tertiary" className="whitespace-nowrap">
      {label}
    </Text>
    <Text size="sm" weight="semibold" className="whitespace-nowrap">
      {value}
    </Text>
  </>
);

export type RangeUnit = 'd' | 'w' | 'm';

/** Calendar-anchored quick picks (current or previous whole week/month). */
export const NAMED_RANGE_PRESETS = [
  'this-week',
  'last-week',
  'this-month',
  'last-month',
] as const;

export type NamedRangePreset = (typeof NAMED_RANGE_PRESETS)[number];

export const NAMED_RANGE_LABELS: Record<NamedRangePreset, string> = {
  'this-week': 'This week',
  'last-week': 'Last week',
  'this-month': 'This month',
  'last-month': 'Last month',
};

export const isNamedRangePreset = (
  value: string | null,
): value is NamedRangePreset =>
  (NAMED_RANGE_PRESETS as readonly string[]).includes(value ?? '');

/** e.g. '3d', '5w', '12m', a named calendar preset, or 'custom' */
export type RangePreset = `${number}${RangeUnit}` | NamedRangePreset | 'custom';

export const DEFAULT_RANGE_PRESET: RangePreset = '3m';

/** AWS-style quick ranges, grouped by unit. */
export const RANGE_PRESET_GROUPS: Array<{
  unit: RangeUnit;
  label: string;
  singular: string;
  plural: string;
  values: number[];
}> = [
  {
    unit: 'd',
    label: 'Days',
    singular: 'day',
    plural: 'days',
    values: [1, 2, 3, 5, 7],
  },
  {
    unit: 'w',
    label: 'Weeks',
    singular: 'week',
    plural: 'weeks',
    values: [1, 2, 3, 4, 6, 8],
  },
  {
    unit: 'm',
    label: 'Months',
    singular: 'month',
    plural: 'months',
    values: [1, 2, 3, 6, 9, 12],
  },
];

export const parseRangePreset = (
  value: string | null,
): { count: number; unit: RangeUnit } | null => {
  const match = value?.match(/^(\d+)([dwm])$/);
  if (!match) {
    return null;
  }
  const count = Number(match[1]);
  const unit = match[2] as RangeUnit;
  const group = RANGE_PRESET_GROUPS.find((entry) => entry.unit === unit);
  return group?.values.includes(count) ? { count, unit } : null;
};

export const rangePresetLabel = (preset: RangePreset): string => {
  if (preset === 'custom') {
    return 'Custom range';
  }
  if (isNamedRangePreset(preset)) {
    return NAMED_RANGE_LABELS[preset];
  }
  const parsed = parseRangePreset(preset);
  if (!parsed) {
    return preset;
  }
  const group = RANGE_PRESET_GROUPS.find(
    (entry) => entry.unit === parsed.unit,
  )!;
  return `Last ${parsed.count} ${parsed.count === 1 ? group.singular : group.plural}`;
};

export const TIMEZONE_OPTIONS: Array<{
  value: ReportingTimezone;
  label: string;
}> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/New_York', label: 'Eastern (ET)' },
];

const isoToLocalDate = (iso: string | undefined): Date | undefined =>
  iso ? new Date(`${iso}T00:00:00`) : undefined;

/** Day view is only offered for windows of at most this many days (a full month). */
export const MAX_DAY_GRAIN_SPAN_DAYS = 31;

/**
 * Week view is only offered while the window fits the server's week bucket
 * cap — past it the window would be clamped and the oldest weeks dropped, so
 * Month is offered instead of a silently shortened week series. Measured in
 * buckets rather than days because the server snaps weeks back to Monday.
 */
export const exceedsWeekGrainCap = (weekBuckets: number): boolean =>
  weekBuckets > REPORTING_GRAIN_MAX_BUCKETS.week;

export const TimeControls: React.FC<{
  preset: RangePreset;
  customFrom: string | undefined;
  customTo: string | undefined;
  timezone: ReportingTimezone;
  grain: ReportingGrain;
  /** Length of the active window in days — gates the Day grain. */
  spanDays: number;
  /** Week buckets the active window would produce — gates the Week grain. */
  weekBuckets: number;
  onPresetChange: (preset: RangePreset) => void;
  onCustomRangeChange: (from: string, to: string) => void;
  onTimezoneChange: (timezone: ReportingTimezone) => void;
  onGrainChange: (grain: ReportingGrain) => void;
}> = ({
  preset,
  customFrom,
  customTo,
  timezone,
  grain,
  spanDays,
  weekBuckets,
  onPresetChange,
  onCustomRangeChange,
  onTimezoneChange,
  onGrainChange,
}) => {
  const dayDisabled = spanDays > MAX_DAY_GRAIN_SPAN_DAYS;
  const weekDisabled = exceedsWeekGrainCap(weekBuckets);
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  // Hold selections locally while the calendar is open; the URL (and the
  // data query) only updates when the picker CLOSES with a complete range,
  // so choosing custom never reloads mid-selection
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();
  const [draftCustom, setDraftCustom] = useState(false);
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const customActive = preset === 'custom' || draftCustom;

  const committedRange: DateRange | undefined =
    customFrom || customTo
      ? { from: isoToLocalDate(customFrom), to: isoToLocalDate(customTo) }
      : undefined;

  const commitDraftOnClose = () => {
    const range =
      draftRange ?? (preset === 'custom' ? committedRange : undefined);
    if (range?.from && range?.to) {
      onCustomRangeChange(
        format(range.from, 'yyyy-MM-dd'),
        format(range.to, 'yyyy-MM-dd'),
      );
      setDraftCustom(false);
      setDraftRange(undefined);
    }
  };

  const customRangeLabel = () => {
    const range =
      draftRange ?? (preset === 'custom' ? committedRange : undefined);
    if (!range?.from) {
      return 'Pick start and end dates';
    }
    return range.to
      ? `${format(range.from, 'PP')} – ${format(range.to, 'PP')}`
      : format(range.from, 'PP');
  };

  const grainLabel: Record<ReportingGrain, string> = {
    day: 'Day',
    week: 'Week',
    month: 'Month',
  };

  // display:contents — the controls join the parent toolbar's wrap flow
  // instead of wrapping as an isolated block
  return (
    <div className="contents">
      <Popover open={rangePickerOpen} onOpenChange={setRangePickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" aria-label="Time period">
            <ControlLabel
              label="Time period"
              value={
                draftCustom && preset !== 'custom'
                  ? 'Custom range'
                  : rangePresetLabel(preset)
              }
            />
            <Icon name="ChevronDown" size="xs" className="text-text-tertiary" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-fit p-4">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-1.5">
              {NAMED_RANGE_PRESETS.map((key) => (
                <Button
                  key={key}
                  variant={preset === key ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-lg"
                  onClick={() => {
                    setDraftCustom(false);
                    onPresetChange(key);
                    setRangePickerOpen(false);
                  }}
                >
                  {NAMED_RANGE_LABELS[key]}
                </Button>
              ))}
            </div>
            <div className="border-t border-border-primary" />
            {RANGE_PRESET_GROUPS.map((group) => (
              <div key={group.unit} className="flex items-center gap-3">
                <Text
                  size="sm"
                  color="tertiary"
                  className="w-16 shrink-0 text-right"
                >
                  {group.label}
                </Text>
                <div className="flex flex-wrap gap-1.5">
                  {group.values.map((count) => {
                    const key = `${count}${group.unit}` as RangePreset;
                    const isActive = preset === key;
                    return (
                      <Button
                        key={key}
                        variant={isActive ? 'default' : 'outline'}
                        size="sm"
                        aria-label={rangePresetLabel(key)}
                        className="size-10 rounded-lg text-sm tabular-nums"
                        onClick={() => {
                          setDraftCustom(false);
                          onPresetChange(key);
                          setRangePickerOpen(false);
                        }}
                      >
                        {count}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex items-center border-t border-border-primary pt-3">
              <Button
                variant={customActive ? 'default' : 'outline'}
                size="sm"
                className="h-10 flex-1 rounded-lg"
                onClick={() => {
                  setDraftCustom(true);
                  setRangePickerOpen(false);
                  setCustomPickerOpen(true);
                }}
              >
                <Icon name="Calendar" size="sm" />
                Custom range…
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {customActive && (
        <Popover
          open={customPickerOpen}
          onOpenChange={(open) => {
            setCustomPickerOpen(open);
            if (!open) {
              commitDraftOnClose();
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" aria-label="Custom date range">
              <Icon name="Calendar" size="sm" className="text-text-tertiary" />
              {customRangeLabel()}
              <Icon
                name="ChevronDown"
                size="xs"
                className="text-text-tertiary"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={
                draftRange ?? (preset === 'custom' ? committedRange : undefined)
              }
              defaultMonth={(draftRange ?? committedRange)?.from ?? new Date()}
              onSelect={(range) => setDraftRange(range)}
              numberOfMonths={2}
              showOutsideDays={false}
              autoFocus
              footer={
                <Stack direction="horizontal" justify="end" align="stretch">
                  <Button
                    disabled={!draftRange?.from || !draftRange?.to}
                    onClick={() => {
                      setCustomPickerOpen(false);
                      commitDraftOnClose();
                    }}
                  >
                    Done
                  </Button>
                </Stack>
              }
            />
          </PopoverContent>
        </Popover>
      )}
      <Select
        value={grain}
        onValueChange={(value) => onGrainChange(value as ReportingGrain)}
      >
        <SelectTrigger className="h-8 w-fit gap-2" aria-label="Group data by">
          <ControlLabel label="Group data by" value={grainLabel[grain]} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="day" disabled={dayDisabled} textValue="Day">
            <div className="flex flex-col">
              <span>Day</span>
              {dayDisabled && (
                <span className="text-xs text-text-tertiary">
                  Needs a time period of {MAX_DAY_GRAIN_SPAN_DAYS} days or less
                </span>
              )}
            </div>
          </SelectItem>
          <SelectItem value="week" disabled={weekDisabled} textValue="Week">
            <div className="flex flex-col">
              <span>Week</span>
              {weekDisabled && (
                <span className="text-xs text-text-tertiary">
                  Needs a time period of {REPORTING_GRAIN_MAX_BUCKETS.week}{' '}
                  weeks or less
                </span>
              )}
            </div>
          </SelectItem>
          <SelectItem value="month">Month</SelectItem>
        </SelectContent>
      </Select>
      <Select
        value={timezone}
        onValueChange={(value) => onTimezoneChange(value as ReportingTimezone)}
      >
        <SelectTrigger className="h-8 w-fit gap-2" aria-label="Timezone">
          <Icon name="Globe" size="sm" className="text-text-tertiary" />
          <ControlLabel
            label="Timezone"
            value={
              TIMEZONE_OPTIONS.find((option) => option.value === timezone)
                ?.label ?? timezone
            }
          />
        </SelectTrigger>
        <SelectContent>
          {TIMEZONE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
